import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { useGetCbtStartHereQuery } from "../../reactWrapper/Redux/apiSlice";
import {
  describeError,
  itemTypeLabel,
  levelLabel,
} from "./cbtQuestionRuns.config";

/**
 * "Start here": the 10 item-levels that most need questions, ranked by
 * talents and open jobs. "Use this" fills the form above with that item and level.
 *
 *   <StartHere onPick={(entry) => ...} />
 */
export default function StartHere({ onPick }) {
  const { data, isLoading, isError, error } = useGetCbtStartHereQuery();

  return (
    <section className="rounded-xl border bg-card p-5">
      <h2 className="font-display text-lg font-semibold text-brand-navy">
        Start here
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Items and levels with the most talents and open jobs that still have
        fewer than 30 questions.
      </p>

      {isLoading && (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Finding which items need questions most…
        </p>
      )}

      {isError && (
        <p className="mt-4 text-sm text-red-700">{describeError(error)}</p>
      )}

      {data && data.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">
          Every item that talents or jobs need already has 30 questions at its
          level.
        </p>
      )}

      {data && data.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[620px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3">#</th>
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3">Level</th>
                <th className="py-2 pr-3 text-right">Talents</th>
                <th className="py-2 pr-3 text-right">Open jobs</th>
                <th className="py-2 pr-3">Questions</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {data.map((entry, index) => (
                <tr
                  key={`${entry.itemType}-${entry.itemId}-${entry.levelCode}`}
                  className="border-t">
                  <td className="py-2 pr-3 font-bold text-muted-foreground">
                    {index + 1}
                  </td>
                  <td className="py-2 pr-3">
                    <span className="block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {itemTypeLabel(entry.itemType)}
                    </span>
                    <span className="font-semibold text-brand-navy">
                      {entry.itemName}
                    </span>
                  </td>
                  <td className="py-2 pr-3">{levelLabel(entry.levelCode)}</td>
                  <td className="py-2 pr-3 text-right">{entry.talents}</td>
                  <td className="py-2 pr-3 text-right">{entry.jobs}</td>
                  <td className="py-2 pr-3">{entry.questions} / 30</td>
                  <td className="py-2 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onPick(entry)}>
                      Use this
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
